/**
 * useRegistrationFlow Hook
 *
 * Main state management hook for the provider registration workflow.
 * Manages the two-phase registration process, form state, and workflow progression.
 *
 * @module hooks/useRegistrationFlow
 */

import { useState, useCallback, useEffect } from 'react';
import { useValidation } from './useValidation';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/ApiService';
import type {
  RegistrationStep,
  RegistrationWorkflowState,
  RegistrationFormState,
  ProfileFormState,
  RegistrationFormHandlers,
  RegistrationApiRequest,
  ProfileUpdateApiRequest
} from '@/types/registration';
import { FileUtils } from '@/utils/fileUtils';
import { getAuthService } from '@/services/auth/AuthServiceFactory';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * Registration flow configuration
 */
export interface RegistrationFlowConfig {
  /** Debounce delay for validation */
  validationDebounce?: number;
  /** Show validation errors immediately */
  showErrorsImmediately?: boolean;
  /** Initial step to start the workflow */
  initialStep?: RegistrationStep;
}

/**
 * Registration flow hook return type
 */
export interface UseRegistrationFlowReturn {
  /** Current step (null = loading) */
  currentStep: RegistrationStep | null;
  /** Current workflow state */
  workflow: RegistrationWorkflowState;
  /** Registration form state */
  registrationForm: RegistrationFormState;
  /** Profile form state */
  profileForm: ProfileFormState;
  /** Form handlers */
  handlers: RegistrationFormHandlers;
  /** Move to next step */
  nextStep: () => void;
  /** Move to previous step */
  previousStep: () => void;
  /** Reset entire workflow */
  resetWorkflow: () => void;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: RegistrationFlowConfig = {
  validationDebounce: 300,
  showErrorsImmediately: false
};


/**
 * Determine initial step based on authentication status
 */
const getInitialStep = async (): Promise<RegistrationStep> => {
  const logger = getLogger();
  try {
    // Check if user is authenticated
    const authService = getAuthService();
    const isAuthenticated = await authService.isAuthenticated();

    if (!isAuthenticated) {
      return 'registration';
    }

    // User is authenticated, check if provider profile exists
    try {
      await apiService.provider.getProfile();
      // Profile exists, go to complete step
      return 'complete';
    } catch {
      return 'profile_update';
    }
  } catch (error) {
    logger.error('Error determining initial step:', error);
    // Default to registration on error
    return 'registration';
  }
};

/**
 * Create initial workflow state
 */
const createInitialWorkflowState = (initialStep?: RegistrationStep): RegistrationWorkflowState => ({
  currentStep: initialStep || 'registration',
  status: initialStep === 'complete' ? 'pending_approval' : 'pending_profile',
  canProceed: false
});

/**
 * Create initial registration form state
 */
const createInitialRegistrationFormState = (): RegistrationFormState => ({
  values: {
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  },
  validation: {
    email: { result: { isValid: true }, touched: false, validating: false },
    password: { result: { isValid: true }, touched: false, validating: false },
    confirmPassword: { result: { isValid: true }, touched: false, validating: false },
    firstName: { result: { isValid: true }, touched: false, validating: false },
    lastName: { result: { isValid: true }, touched: false, validating: false }
  },
  isValid: false,
  isSubmitting: false
});

/**
 * Create initial profile form state
 */
const createInitialProfileFormState = (): ProfileFormState => ({
  values: {
    firstName: '',
    lastName: '',
    role: '',
    providerNumber: '',
    practiceName: '',
    practiceAddress: '',
    bio: '',
    photo: undefined,
    consent: {
      dataProcessing: true, // default true
      termsOfService: true, // default true
      privacyPolicy: true, // default true
      biometricProcessing: true // default false
    }
  },
  validation: {
    firstName: { result: { isValid: true }, touched: false, validating: false },
    lastName: { result: { isValid: true }, touched: false, validating: false },
    role: { result: { isValid: true }, touched: false, validating: false },
    providerNumber: { result: { isValid: true }, touched: false, validating: false },
    practiceName: { result: { isValid: true }, touched: false, validating: false },
    consent: { result: { isValid: true }, touched: false, validating: false }
  },
  isValid: false,
  isSubmitting: false
});

/**
 * Custom hook for managing the provider registration workflow
 *
 * @param config - Configuration options
 * @returns Registration flow state and handlers
 *
 * @example
 * ```typescript
 * const {
 *   workflow,
 *   registrationForm,
 *   profileForm,
 *   handlers
 * } = useRegistrationFlow();
 *
 * // Handle registration form submission
 * await handlers.onRegistrationSubmit(registrationForm.values);
 * ```
 */
export const useRegistrationFlow = (
  config: RegistrationFlowConfig = {}
): UseRegistrationFlowReturn => {
  const logger = getLogger();
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  const { signIn } = useAuth();

  // State
  const [currentStep, setCurrentStep] = useState<RegistrationStep | null>(null);
  const [workflow, setWorkflow] = useState<RegistrationWorkflowState>(
    createInitialWorkflowState(mergedConfig.initialStep)
  );
  const [registrationForm, setRegistrationForm] = useState<RegistrationFormState>(createInitialRegistrationFormState);
  const [profileForm, setProfileForm] = useState<ProfileFormState>(createInitialProfileFormState);

  // Determine initial step on mount
  useEffect(() => {
    const determineStep = async () => {
      const initialStep = await getInitialStep();
      setCurrentStep(initialStep);
      setWorkflow(createInitialWorkflowState(initialStep));
    };

    determineStep();
  }, []);

  // Validation hooks
  const registrationValidation = useValidation(
    ['email', 'password', 'confirmPassword', 'firstName', 'lastName'],
    {
      debounceMs: mergedConfig.validationDebounce,
      showErrorsImmediately: mergedConfig.showErrorsImmediately
    }
  );

  const profileValidation = useValidation(
    ['firstName', 'lastName', 'providerNumber', 'practiceName', 'role',],
    {
      debounceMs: mergedConfig.validationDebounce,
      showErrorsImmediately: mergedConfig.showErrorsImmediately
    }
  );

  // Update form validation state from validation hooks
  useEffect(() => {
    setRegistrationForm(prev => ({
      ...prev,
      validation: registrationValidation.validationState as RegistrationFormState['validation'],
      isValid: registrationValidation.isFormValid
    }));
  }, [registrationValidation.validationState, registrationValidation.isFormValid]);

  useEffect(() => {
    setProfileForm(prev => ({
      ...prev,
      validation: profileValidation.validationState as ProfileFormState['validation'],
      isValid: profileValidation.isFormValid
    }));
  }, [profileValidation.validationState, profileValidation.isFormValid]);

  // Update workflow canProceed based on current step validation
  useEffect(() => {
    const canProceed = workflow.currentStep === 'registration'
      ? registrationForm.isValid
      : profileForm.isValid;

    setWorkflow(prev => ({
      ...prev,
      canProceed
    }));
  }, [workflow.currentStep, registrationForm.isValid, profileForm.isValid]);

  /**
   * Handle registration form field changes
   */
  const handleRegistrationFieldChange = useCallback((field: string, value: string) => {
    logger.debug('handleRegistrationFieldChange: ', field, value);
    setRegistrationForm(prev => ({
      ...prev,
      values: {
        ...prev.values,
        [field]: value
      }
    }));

    // Validate field with context
    registrationValidation.validateField(field, value, registrationForm.values);
  }, [registrationValidation, registrationForm.values]);

  /**
   * Handle profile form field changes
   */
  const handleProfileFieldChange = useCallback((field: string, value: string | object) => {
    setProfileForm(prev => ({
      ...prev,
      values: {
        ...prev.values,
        [field]: value
      }
    }));

    // For phone field, pass the phone object as string for validation
    const validationValue = typeof value === 'object' && value !== null
      ? (value as { number?: string }).number || ''
      : String(value);

    const context = typeof value === 'object' && value !== null
      ? { ...profileForm.values, ...(value as Record<string, unknown>) }
      : { ...profileForm.values, country: 'AU' };

    profileValidation.validateField(field, validationValue, context);
  }, [profileValidation, profileForm.values]);

  /**
   * Handle field blur events
   */
  const handleFieldBlur = useCallback((field: string) => {
    if (workflow.currentStep === 'registration') {
      registrationValidation.setFieldTouched(field, true);
    } else {
      profileValidation.setFieldTouched(field, true);
    }
  }, [workflow.currentStep, registrationValidation, profileValidation]);

  /**
   * Handle registration form submission
   */
  const handleRegistrationSubmit = useCallback(async (data: RegistrationFormState['values']) => {
    setRegistrationForm(prev => ({ ...prev, isSubmitting: true, submitError: undefined }));

    try {
      // Validate all fields first
      const isValid = await registrationValidation.validateAll(data);
      if (!isValid) {
        throw new Error('Please fix validation errors before submitting');
      }

      // Prepare API request
      const apiRequest: RegistrationApiRequest = {
        email: data.email.trim(),
        password: data.password,
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        role: 'provider'
      };

      // Make actual API call
      const response = await apiService.provider.register(apiRequest);

      // Log in after successful registration
      await signIn({
        email: apiRequest.email,
        password: apiRequest.password
      });

      // Update workflow state with real provider ID from API response
      setCurrentStep('profile_update');
      setWorkflow(prev => ({
        ...prev,
        currentStep: 'profile_update',
        providerId: response.user_id,
        canProceed: false
      }));

      // Clear registration form submission state
      setRegistrationForm(prev => ({ ...prev, isSubmitting: false }));

      profileForm.values['firstName'] = apiRequest.first_name;
      profileForm.values['lastName'] = apiRequest.last_name;
      setProfileForm(prev => ({ ...prev, values: profileForm.values }));

    } catch (error) {
      setRegistrationForm(prev => ({
        ...prev,
        isSubmitting: false,
        submitError: error instanceof Error ? error.message : 'Registration failed'
      }));
    }
  }, [registrationValidation]);

  /**
   * Handle profile form submission
   */
  const handleProfileSubmit = useCallback(async (data: ProfileFormState['values']) => {
    setProfileForm(prev => ({ ...prev, isSubmitting: true, submitError: undefined }));

    try {
      // Validate all fields first
      const isValid = await profileValidation.validateAll({
        firstName: data.firstName,
        lastName: data.lastName,
        providerNumber: data.providerNumber,
        practiceName: data.practiceName,
        practiceAddress: data.practiceAddress,
        role: data.role,
      });

      if (!isValid) {
        throw new Error('Please fix validation errors before submitting');
      }

      logger.debug('registrationForm.values ', registrationForm.values);

      // Convert photo to base64 if provided
      let photoData = undefined;
      if (data.photo) {
        photoData = await FileUtils.safeConvertToBase64(data.photo);
      }

      // Prepare API request
      const apiRequest: ProfileUpdateApiRequest = {
        first_name: data.firstName,
        last_name: data.lastName,
        role: data.role,
        bio: data.bio?.trim(),
        provider_number: data.providerNumber.trim(),
        practice_name: data.practiceName.trim(),
        practice_address: data.practiceAddress.trim(),
        photo: photoData,
        consent: {
          data_processing: true,
          terms_of_service: true,
          privacy_policy: true,
          biometric_processing: true
        }
      };

      // Make actual API call
      logger.debug('Profile update API request:', apiRequest);

      const response = await apiService.provider.updateProfile(apiRequest);
      logger.debug('Profile update API response:', response);

      // Update workflow state
      setCurrentStep('complete');
      setWorkflow(prev => ({
        ...prev,
        currentStep: 'complete',
        status: 'pending_approval',
        canProceed: true
      }));

      // Clear profile form submission state
      setProfileForm(prev => ({ ...prev, isSubmitting: false }));

    } catch (error) {
      setProfileForm(prev => ({
        ...prev,
        isSubmitting: false,
        submitError: error instanceof Error ? error.message : 'Profile update failed'
      }));
    }
  }, [profileValidation]);

  /**
   * Move to next step
   */
  const nextStep = useCallback(() => {
    if (!workflow.canProceed) return;

    const stepOrder: RegistrationStep[] = ['registration', 'profile_update', 'email_verification', 'complete'];
    const currentIndex = stepOrder.indexOf(workflow.currentStep);
    const nextIndex = Math.min(currentIndex + 1, stepOrder.length - 1);
    const nextStepValue = stepOrder[nextIndex];

    setCurrentStep(nextStepValue);
    setWorkflow(prev => ({
      ...prev,
      currentStep: nextStepValue,
      canProceed: false
    }));
  }, [workflow.canProceed, workflow.currentStep]);

  /**
   * Move to previous step
   */
  const previousStep = useCallback(() => {
    const stepOrder: RegistrationStep[] = ['registration', 'profile_update', 'email_verification', 'complete'];
    const currentIndex = stepOrder.indexOf(workflow.currentStep);
    const prevIndex = Math.max(currentIndex - 1, 0);
    const prevStepValue = stepOrder[prevIndex];

    setCurrentStep(prevStepValue);
    setWorkflow(prev => ({
      ...prev,
      currentStep: prevStepValue,
      canProceed: true
    }));
  }, [workflow.currentStep]);

  /**
   * Reset entire workflow
   */
  const resetWorkflow = useCallback(() => {
    setCurrentStep(null);
    setWorkflow(createInitialWorkflowState());
    setRegistrationForm(createInitialRegistrationFormState());
    setProfileForm(createInitialProfileFormState());
    registrationValidation.clearAll();
    profileValidation.clearAll();

    // Re-determine initial step
    const determineStep = async () => {
      const initialStep = await getInitialStep();
      setCurrentStep(initialStep);
      setWorkflow(createInitialWorkflowState(initialStep));
    };
    determineStep();
  }, [registrationValidation, profileValidation]);

  // Form handlers
  const handlers: RegistrationFormHandlers = {
    onRegistrationSubmit: handleRegistrationSubmit,
    onProfileSubmit: handleProfileSubmit,
    onFieldChange: workflow.currentStep === 'registration'
      ? handleRegistrationFieldChange
      : handleProfileFieldChange,
    onFieldBlur: handleFieldBlur,
    onReset: resetWorkflow
  };

  return {
    currentStep,
    workflow,
    registrationForm,
    profileForm,
    handlers,
    nextStep,
    previousStep,
    resetWorkflow
  };
};

export default useRegistrationFlow;